import React from "react";
import Modal from "@amzn/awsui-components-console/modal";
import Box from "@amzn/awsui-components-console/box";
import Button from "@amzn/awsui-components-console/button";
import SpaceBetween from "@amzn/awsui-components-console/space-between";
import Slider from "@amzn/awsui-components-console/slider";
import FormField from "@amzn/awsui-components-console/form-field";
import SegmentedControl from "@amzn/awsui-components-console/segmented-control";
import {
  useSettingsStore,
  PanelBehavior,
  ThemeMode,
} from "src/stores/useSettingsStore";

const BEHAVIOR_LABELS: Record<number, string> = {
  0: "Never",
  1: "Page Dependent",
  2: "Always",
};

const THEME_OPTIONS: { id: ThemeMode; text: string }[] = [
  { id: "system", text: "System" },
  { id: "light", text: "Light" },
  { id: "dark", text: "Dark" },
];

interface SettingsModalProps {
  visible: boolean;
  onDismiss: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  visible,
  onDismiss,
}) => {
  const {
    navPanelBehavior,
    toolsPanelBehavior,
    themeMode,
    setNavPanelBehavior,
    setToolsPanelBehavior,
    setThemeMode,
  } = useSettingsStore();

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Settings"
      closeAriaLabel="Close settings"
      size="medium"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            Close
          </Button>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <FormField
          label="Theme"
          description="System follows your operating system's light/dark setting"
        >
          <SegmentedControl
            selectedId={themeMode}
            label="Theme"
            options={THEME_OPTIONS}
            onChange={({ detail }) =>
              setThemeMode(detail.selectedId as ThemeMode)
            }
          />
        </FormField>
        <FormField
          label="Navigation (left) panel default state"
          description="Controls whether the navigation (left) panel is open when you navigate to a page"
        >
          <Slider
            value={navPanelBehavior}
            min={0}
            max={2}
            step={1}
            tickMarks
            referenceValues={[1]}
            valueFormatter={(value) => BEHAVIOR_LABELS[value] ?? ""}
            ariaLabel="Navigation panel behavior"
            onChange={({ detail }) =>
              setNavPanelBehavior(detail.value as PanelBehavior)
            }
          />
        </FormField>
        <FormField
          label="Tools (right) panel default state"
          description="Controls whether the tools (right) panel is open when you navigate to a page"
        >
          <Slider
            value={toolsPanelBehavior}
            min={0}
            max={2}
            step={1}
            tickMarks
            referenceValues={[1]}
            valueFormatter={(value) => BEHAVIOR_LABELS[value] ?? ""}
            ariaLabel="Tools panel behavior"
            onChange={({ detail }) =>
              setToolsPanelBehavior(detail.value as PanelBehavior)
            }
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
